// src/pages/payments/PaymentVerificationPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  Save,
  Upload,
  Paperclip,
  Trash2,
  RefreshCw,
  Calendar,
  Download,
  Loader2,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Ban,
  MessageSquare,
  X
} from 'lucide-react';
import { PageHeader, DataTable, FilterPanel } from '../../components/common';
import {
  getPaymentRecords,
  updatePaymentRecord,
  verifyPaymentRecord,
  bulkVerifyPaymentRecords,
  voidPaymentRecord,
  flagPaymentRecordIssue,
  resolvePaymentRecordIssue,
  uploadOrderPaymentProof
} from '../../services/api/paymentVerification';
import { getPaymentMethods, getPaymentTypes } from '../../services/api/orderReference';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatCurrency } from '../../utils/currencyUtil';
import { formatDate, formatTime, formatDateTime } from '../../utils/dateUtils';
import { getImageUrl } from '../../utils/imageUtils';
import { isBankTransferMissingRef } from '../../utils/paymentRecordUtils';
import { PaymentMethodIcon } from '../../utils/paymentMethodIcons';
import DateRangePicker from '../../components/ui/DateRangePicker/DateRangePicker';
import PaymentReasonModal from './components/PaymentReasonModal';
import PaymentRecordNotesModal from './components/PaymentRecordNotesModal';
import styles from './PaymentVerificationPage.module.css';

const VALID_PROOF_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
const MAX_PROOF_SIZE = 5 * 1024 * 1024; // 5MB
const isPdfUrl = (url) => !!url && url.toLowerCase().endsWith('.pdf');

// ─── Export Progress Modal ────────────────────────────────────────────────────
const ExportProgressModal = ({ isOpen, onClose, status, error, totalRecords, fetchedRecords }) => {
  if (!isOpen) return null;

  const pct       = totalRecords > 0 ? Math.round((fetchedRecords / totalRecords) * 100) : 0;
  const isDone    = status === 'done';
  const isFailed  = status === 'error';
  const isRunning = status === 'running';

  return (
    <div className={styles.exportModalOverlay}>
      <div className={styles.exportModal}>
        <div className={styles.exportModalHeader}>
          <div className={styles.exportModalTitle}>
            <Download size={20} />
            <span>Exporting Payment Records</span>
          </div>
          {(isDone || isFailed) && (
            <button onClick={onClose} className={styles.exportModalClose}><X size={18} /></button>
          )}
        </div>

        <div className={styles.exportModalBody}>
          {isRunning && <div className={styles.exportSpinner}><Loader2 size={32} className={styles.spinnerIcon} /></div>}
          {isDone    && <div className={styles.exportSuccessIcon}><CheckCircle size={32} /></div>}
          {isFailed  && <div className={styles.exportErrorIcon}><AlertCircle size={32} /></div>}

          <p className={styles.exportStatusText}>
            {isFailed
              ? error || 'Export failed. Please try again.'
              : isDone
              ? 'Export complete! Your file has been downloaded.'
              : `Fetching payment records… ${fetchedRecords.toLocaleString()} of ${totalRecords > 0 ? totalRecords.toLocaleString() : '?'}`}
          </p>

          {!isFailed && totalRecords > 0 && (
            <>
              <div className={styles.exportProgressBar}>
                <div
                  className={`${styles.exportProgressFill} ${isDone ? styles.exportProgressDone : ''}`}
                  style={{ width: `${isDone ? 100 : pct}%` }}
                />
              </div>
              <p className={styles.exportProgressPct}>{isDone ? 100 : pct}%</p>
            </>
          )}

          {!isFailed && totalRecords === 0 && isRunning && (
            <div className={styles.exportProgressBar}>
              <div className={styles.exportProgressIndeterminate} />
            </div>
          )}
        </div>

        {(isDone || isFailed) && (
          <div className={styles.exportModalFooter}>
            <button onClick={onClose} className={styles.secondaryButton}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
};

const PaymentVerificationPage = () => {
  const { t } = useLocalization();
  const { success, error: showError, confirm } = useAlert();
  const { hasPermission } = usePermissions();
  const canVoid = hasPermission('void_payment_records');
  const canVerify = hasPermission('verify_payment_records');

  const [paymentRecords, setPaymentRecords] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const [selectedIds, setSelectedIds] = useState([]);
  const [editBuffers, setEditBuffers] = useState({});
  const [uploadingId, setUploadingId] = useState(null);
  const [proofErrors, setProofErrors] = useState({});

  // Void / flag-issue / resolve reason modal, and notes modal
  const [reasonModal, setReasonModal] = useState({ mode: null, record: null });
  const [isReasonSubmitting, setIsReasonSubmitting] = useState(false);
  const [notesModalRecord, setNotesModalRecord] = useState(null);

  // Export state
  const [exportModalOpen, setExportModalOpen]           = useState(false);
  const [exportStatus, setExportStatus]                 = useState('idle');
  const [exportError, setExportError]                   = useState('');
  const [exportTotalRecords, setExportTotalRecords]     = useState(0);
  const [exportFetchedRecords, setExportFetchedRecords] = useState(0);
  const exportAbortRef = useRef(false);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    total: 0,
    pageSizeOptions: [10, 20, 50, 100]
  });

  const [sorting, setSorting] = useState({
    orderBy: 'createdAt',
    orderDirection: 'DESC'
  });

  const [filters, setFilters] = useState({
    paymentMethodId: '',
    paymentTypeId: '',
    status: 'unverified',
    search: '',
    paymentDateRange: { from: '', to: '' }
  });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [methodsData, typesData] = await Promise.all([getPaymentMethods(), getPaymentTypes()]);
        setPaymentMethods(methodsData.paymentMethods || []);
        setPaymentTypes(typesData.paymentTypes || []);
      } catch (err) {
        console.error('Failed to load payment reference data:', err);
      }
    };
    loadReferenceData();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setIsInitialized(true), 0);
    return () => clearTimeout(timer);
  }, []);

  // Build filter params shared by fetch + export
  const buildFilterParams = () => ({
    paymentMethodId: filters.paymentMethodId || undefined,
    paymentTypeId: filters.paymentTypeId || undefined,
    status: filters.status === 'all' ? undefined : filters.status,
    search: filters.search || undefined,
    paymentDateFrom: filters.paymentDateRange?.from || undefined,
    paymentDateTo: filters.paymentDateRange?.to || undefined
  });

  const fetchPaymentRecords = useCallback(async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);

      const response = await getPaymentRecords({
        page: pagination.currentPage,
        limit: pagination.pageSize,
        orderBy: sorting.orderBy,
        orderDirection: sorting.orderDirection,
        ...buildFilterParams()
      });

      setPaymentRecords(response.paymentRecords || []);
      setPagination((prev) => ({ ...prev, total: response.pagination?.total || 0 }));
      setEditBuffers({});
    } catch (err) {
      console.error('Failed to fetch payment records:', err);
      if (err.response?.status === 403) {
        setLoadingError(t('errors.permissionDenied'));
      } else {
        setLoadingError(t('paymentVerification.failedToLoad'));
      }
    } finally {
      setIsLoading(false);
    }
  }, [pagination.currentPage, pagination.pageSize, sorting.orderBy, sorting.orderDirection, filters, t]);

  useEffect(() => {
    if (!isInitialized) return;
    fetchPaymentRecords();
  }, [isInitialized, fetchPaymentRecords]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSearchChange = (value) => {
    setFilters((prev) => ({ ...prev, search: value }));
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleClearFilters = () => {
    setFilters({
      paymentMethodId: '',
      paymentTypeId: '',
      status: 'unverified',
      search: '',
      paymentDateRange: { from: '', to: '' }
    });
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageSizeChange = (newPageSize) => {
    setPagination((prev) => ({ ...prev, pageSize: newPageSize, currentPage: 1 }));
  };

  // ─── Export ───────────────────────────────────────────────────────────────
  const MAX_EXPORT_RECORDS = 150000;
  const EXPORT_BATCH_SIZE  = 500;

  const handleExport = async () => {
    setExportModalOpen(true);
    setExportStatus('running');
    setExportError('');
    setExportTotalRecords(0);
    setExportFetchedRecords(0);
    exportAbortRef.current = false;

    try {
      const filterParams = buildFilterParams();
      const firstPage    = await getPaymentRecords({ ...filterParams, page: 1, limit: 1 });
      const total        = Math.min(firstPage.pagination?.total || 0, MAX_EXPORT_RECORDS);

      if (total === 0) {
        setExportStatus('error');
        setExportError('No payment records to export with the current filters.');
        return;
      }

      setExportTotalRecords(total);
      const allRecords = [];
      const totalPages = Math.ceil(total / EXPORT_BATCH_SIZE);

      for (let page = 1; page <= totalPages; page++) {
        if (exportAbortRef.current) return;
        const response = await getPaymentRecords({ ...filterParams, page, limit: EXPORT_BATCH_SIZE });
        allRecords.push(...(response.paymentRecords || []));
        setExportFetchedRecords(Math.min(allRecords.length, total));
      }

      const headers = [
        'Order Number', 'Payment Method', 'Payment Type', 'Amount', 'Reference',
        'Payment Date', 'Proof File', 'Status', 'Verified By', 'Verified Date',
        'Voided By', 'Voided Date', 'Created By', 'Created Date'
      ];

      const escapeCell = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;

      const rows = allRecords.map(record => [
        record.EcomOrder?.orderNumber || '',
        record.PaymentMethod?.label || '',
        record.EcomOrder?.PaymentType?.label || record.EcomOrder?.PaymentType?.key || '',
        record.paymentAmount || 0,
        record.reference || '',
        record.paymentDate ? formatDate(record.paymentDate) : '',
        record.proofUrl || '',
        record.status || (record.isVerified ? 'verified' : 'unverified'),
        record.verifiedBy?.name || '',
        record.verifiedAt ? formatDateTime(record.verifiedAt) : '',
        record.voidedBy?.name || '',
        record.voidedAt ? formatDateTime(record.voidedAt) : '',
        record.createdBy?.name || '',
        formatDateTime(record.createdAt)
      ].map(escapeCell).join(','));

      const csvContent = [headers.map(escapeCell).join(','), ...rows].join('\n');
      const blob = new Blob(['﻿' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url  = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href     = url;
      link.download = `payment-verification-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setExportStatus('done');
    } catch (err) {
      console.error('Export error:', err);
      setExportStatus('error');
      setExportError('Failed to export payment records. Please try again.');
    }
  };

  const handleCloseExportModal = () => {
    exportAbortRef.current = true;
    setExportModalOpen(false);
    setExportStatus('idle');
  };

  const getFieldValue = (record, field) => {
    if (editBuffers[record.id] && editBuffers[record.id][field] !== undefined) {
      return editBuffers[record.id][field];
    }
    return record[field] ?? '';
  };

  const isDirty = (record) => {
    const buffer = editBuffers[record.id];
    if (!buffer) return false;
    return (
      (buffer.reference !== undefined && buffer.reference !== (record.reference || '')) ||
      (buffer.paymentDate !== undefined && buffer.paymentDate !== (record.paymentDate || ''))
    );
  };

  const isLocked = (record) => record.isVerified || record.status === 'void';

  const handleFieldChange = (recordId, field, value) => {
    setEditBuffers((prev) => ({
      ...prev,
      [recordId]: { ...prev[recordId], [field]: value }
    }));
  };

  const replaceRecordInState = (updatedRecord) => {
    setPaymentRecords((prev) =>
      prev.map((r) => (r.id === updatedRecord.id ? { ...r, ...updatedRecord } : r))
    );
  };

  const handleSaveEdit = async (record) => {
    const buffer = editBuffers[record.id];
    if (!buffer) return;

    try {
      const payload = {};
      if (buffer.reference !== undefined) payload.reference = buffer.reference;
      if (buffer.paymentDate !== undefined) payload.paymentDate = buffer.paymentDate;

      const response = await updatePaymentRecord(record.id, payload);
      replaceRecordInState(response.paymentRecord);
      setEditBuffers((prev) => {
        const next = { ...prev };
        delete next[record.id];
        return next;
      });
      success(t('paymentVerification.updateSuccess'));
    } catch (err) {
      console.error('Failed to update payment record:', err);
      showError(err.response?.data?.message || t('paymentVerification.updateFailed'));
    }
  };

  const handleProofChange = async (record, e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!VALID_PROOF_TYPES.includes(file.type)) {
      setProofErrors((prev) => ({ ...prev, [record.id]: t('ecomOrders.invalidProofFormat') || 'Only .jpg, .png or .pdf files are allowed' }));
      return;
    }
    if (file.size > MAX_PROOF_SIZE) {
      setProofErrors((prev) => ({ ...prev, [record.id]: t('ecomOrders.proofTooLarge') || 'File must be under 5MB' }));
      return;
    }

    setProofErrors((prev) => ({ ...prev, [record.id]: '' }));
    setUploadingId(record.id);
    try {
      const proofUrl = await uploadOrderPaymentProof(file);
      const response = await updatePaymentRecord(record.id, { proofUrl });
      replaceRecordInState(response.paymentRecord);
    } catch (err) {
      console.error('Payment proof upload failed:', err);
      setProofErrors((prev) => ({ ...prev, [record.id]: t('paymentVerification.proofUploadFailed') }));
    } finally {
      setUploadingId(null);
    }
  };

  const handleDeleteProof = async (record) => {
    const confirmed = await confirm(t('paymentVerification.confirmDeleteProof'), {
      title: t('paymentVerification.deleteProof'),
      confirmText: t('paymentVerification.deleteProof'),
      cancelText: t('common.cancel')
    });
    if (!confirmed) return;

    try {
      const response = await updatePaymentRecord(record.id, { proofUrl: null });
      replaceRecordInState(response.paymentRecord);
      success(t('paymentVerification.updateSuccess'));
    } catch (err) {
      console.error('Failed to delete payment proof:', err);
      showError(err.response?.data?.message || t('paymentVerification.updateFailed'));
    }
  };

  const getVerifyBlockReason = (record) => {
    if (isDirty(record)) return t('paymentVerification.saveChangesBeforeVerify');
    if (isBankTransferMissingRef(record)) return t('paymentVerification.referenceRequiredForBankTransfer');
    return null;
  };

  const handleVerifyOne = async (record) => {
    const blockReason = getVerifyBlockReason(record);
    if (blockReason) {
      showError(blockReason);
      return;
    }

    const confirmed = await confirm(t('paymentVerification.confirmVerify'), {
      title: t('paymentVerification.verify'),
      confirmText: t('paymentVerification.verify'),
      cancelText: t('common.cancel')
    });
    if (!confirmed) return;

    try {
      const response = await verifyPaymentRecord(record.id);
      replaceRecordInState(response.paymentRecord);
      setSelectedIds((prev) => prev.filter((id) => id !== record.id));
      success(t('paymentVerification.verifySuccess'));
    } catch (err) {
      console.error('Failed to verify payment record:', err);
      showError(err.response?.data?.message || t('paymentVerification.verifyFailed'));
    }
  };

  const unverifiedSelectedIds = selectedIds.filter((id) => {
    const record = paymentRecords.find((r) => r.id === id);
    return record && record.status !== 'verified' && record.status !== 'void';
  });

  const handleBulkVerify = async () => {
    if (unverifiedSelectedIds.length === 0) {
      showError(t('paymentVerification.noUnverifiedSelected'));
      return;
    }

    const unsavedCount = unverifiedSelectedIds.filter((id) =>
      isDirty(paymentRecords.find((r) => r.id === id))
    ).length;
    if (unsavedCount > 0) {
      showError(t('paymentVerification.bulkUnsavedChanges', { count: unsavedCount }));
      return;
    }

    const missingRefCount = unverifiedSelectedIds.filter((id) =>
      isBankTransferMissingRef(paymentRecords.find((r) => r.id === id))
    ).length;
    if (missingRefCount > 0) {
      showError(t('paymentVerification.bulkReferenceMissing', { count: missingRefCount }));
      return;
    }

    const confirmed = await confirm(
      t('paymentVerification.confirmBulkVerify', { count: unverifiedSelectedIds.length }),
      {
        title: t('paymentVerification.verifySelected', { count: unverifiedSelectedIds.length }),
        confirmText: t('paymentVerification.verify'),
        cancelText: t('common.cancel')
      }
    );
    if (!confirmed) return;

    try {
      const result = await bulkVerifyPaymentRecords(unverifiedSelectedIds);
      setSelectedIds([]);
      await fetchPaymentRecords();

      if (result.summary.failureCount === 0) {
        success(t('paymentVerification.bulkVerifySuccess', { count: result.summary.successCount }));
      } else {
        showError(
          t('paymentVerification.bulkVerifyPartial', {
            successCount: result.summary.successCount,
            totalSubmitted: result.summary.totalSubmitted,
            failureCount: result.summary.failureCount
          })
        );
      }
    } catch (err) {
      console.error('Bulk verify failed:', err);
      showError(err.response?.data?.message || t('paymentVerification.bulkVerifyFailed'));
    }
  };

  const openVoidModal = (record) => setReasonModal({ mode: 'void', record });
  const openFlagIssueModal = (record) => setReasonModal({ mode: 'flagIssue', record });
  const openResolveModal = (record) => setReasonModal({ mode: 'resolve', record });
  const closeReasonModal = () => setReasonModal({ mode: null, record: null });

  const handleReasonConfirm = async (reasonText) => {
    const { mode, record } = reasonModal;
    if (!mode || !record) return;

    setIsReasonSubmitting(true);
    try {
      let response;
      if (mode === 'void') {
        response = await voidPaymentRecord(record.id, reasonText);
      } else if (mode === 'flagIssue') {
        response = await flagPaymentRecordIssue(record.id, reasonText);
      } else if (mode === 'resolve') {
        response = await resolvePaymentRecordIssue(record.id, reasonText);
      }

      replaceRecordInState(response.paymentRecord);
      setSelectedIds((prev) => prev.filter((id) => id !== record.id));
      closeReasonModal();

      if (mode === 'void') success(t('paymentVerification.voidSuccess'));
      if (mode === 'flagIssue') success(t('paymentVerification.flagIssueSuccess'));
      if (mode === 'resolve') success(t('paymentVerification.resolveSuccess'));
    } catch (err) {
      console.error(`Failed to ${mode} payment record:`, err);
      const fallback = {
        void: t('paymentVerification.voidFailed'),
        flagIssue: t('paymentVerification.flagIssueFailed'),
        resolve: t('paymentVerification.resolveFailed')
      }[mode];
      showError(err.response?.data?.message || fallback);
    } finally {
      setIsReasonSubmitting(false);
    }
  };

  const reasonModalConfig = {
    void: {
      title: t('paymentVerification.void'),
      description: t('paymentVerification.confirmVoid'),
      label: t('paymentVerification.voidReasonLabel'),
      confirmLabel: t('paymentVerification.void'),
      required: true
    },
    flagIssue: {
      title: t('paymentVerification.flagAsIssue'),
      description: t('paymentVerification.flagIssueDescription'),
      label: t('paymentVerification.flagIssueReasonLabel'),
      confirmLabel: t('paymentVerification.flagAsIssue'),
      required: true
    },
    resolve: {
      title: t('paymentVerification.resolve'),
      description: t('paymentVerification.resolveDescription'),
      label: t('paymentVerification.resolveNoteLabel'),
      confirmLabel: t('paymentVerification.resolve'),
      required: false
    }
  }[reasonModal.mode] || {};

  const paymentMethodFilterOptions = paymentMethods.map((m) => ({ value: String(m.id), label: m.label }));
  const paymentTypeFilterOptions = paymentTypes.map((pt) => ({ value: String(pt.id), label: pt.label }));

  const filterDefinitions = [
    {
      key: 'paymentMethodId',
      label: t('paymentVerification.paymentMethod'),
      type: 'select',
      placeholder: t('paymentVerification.allPaymentMethods'),
      options: paymentMethodFilterOptions
    },
    {
      key: 'paymentTypeId',
      label: t('paymentVerification.paymentType'),
      type: 'select',
      placeholder: t('paymentVerification.allPaymentTypes'),
      options: paymentTypeFilterOptions
    },
    {
      key: 'status',
      label: t('paymentVerification.isVerified'),
      type: 'select',
      placeholder: t('paymentVerification.allStatuses'),
      options: [
        { value: 'unverified', label: t('paymentVerification.unverified') },
        { value: 'issues', label: t('paymentVerification.issues') },
        { value: 'verified', label: t('paymentVerification.verified') },
        { value: 'void', label: t('paymentVerification.void') }
      ]
    },
    {
      key: 'paymentDateRange',
      label: t('paymentVerification.paymentDate'),
      type: 'custom',
      render: (value, onChange) => (
        <DateRangePicker value={value || { from: '', to: '' }} onChange={onChange} />
      )
    }
  ];

  const columns = [
    {
      key: 'orderNumber',
      header: t('paymentVerification.orderNumber'),
      width: '130px',
      render: (record) => (
        <Link
          to={`/orders/${record.EcomOrder?.id}`}
          className={styles.orderLink}
          onClick={(e) => e.stopPropagation()}
        >
          {record.EcomOrder?.orderNumber}
        </Link>
      )
    },
    {
      key: 'paymentMethod',
      header: t('paymentVerification.paymentMethod'),
      render: (record) => (
        <div className={styles.methodCell}>
          <PaymentMethodIcon iconFileName={record.PaymentMethod?.iconFileName} label={record.PaymentMethod?.label} />
        </div>
      )
    },
    {
      key: 'paymentType',
      header: t('paymentVerification.paymentType'),
      width: '90px',
      render: (record) => (
        <span className={styles.paymentTypeBadge}>
          {record.EcomOrder?.PaymentType?.key?.toUpperCase() || '-'}
        </span>
      )
    },
    {
      key: 'paymentAmount',
      header: t('paymentVerification.amount'),
      sortable: true,
      align: 'right',
      width: '110px',
      render: (record) => formatCurrency(record.paymentAmount)
    },
    {
      key: 'reference',
      header: t('paymentVerification.reference'),
      sortable: true,
      width: '160px',
      render: (record) => (
        <input
          type="text"
          className={styles.inlineInput}
          value={getFieldValue(record, 'reference')}
          disabled={isLocked(record)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleFieldChange(record.id, 'reference', e.target.value)}
        />
      )
    },
    {
      key: 'paymentDate',
      header: t('paymentVerification.paymentDate'),
      sortable: true,
      width: '150px',
      render: (record) => (
        <input
          type="date"
          className={styles.inlineInput}
          value={getFieldValue(record, 'paymentDate')}
          disabled={isLocked(record)}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => handleFieldChange(record.id, 'paymentDate', e.target.value)}
        />
      )
    },
    {
      key: 'proof',
      header: t('paymentVerification.proof'),
      width: '160px',
      render: (record) => (
        <div className={styles.proofCell} onClick={(e) => e.stopPropagation()}>
          {record.proofUrl ? (
            <>
              {isPdfUrl(record.proofUrl) ? (
                <a href={getImageUrl(record.proofUrl)} target="_blank" rel="noopener noreferrer" className={styles.proofFileLink}>
                  <Paperclip size={14} />
                  {t('paymentVerification.viewProof')}
                </a>
              ) : (
                <a href={getImageUrl(record.proofUrl)} target="_blank" rel="noopener noreferrer" className={styles.proofThumbnailLink}>
                  <img src={getImageUrl(record.proofUrl)} alt="Payment proof" className={styles.proofThumbnail} />
                </a>
              )}
              {!isLocked(record) && (
                <div className={styles.proofActions}>
                  <button
                    type="button"
                    className={styles.proofIconButton}
                    title={t('paymentVerification.changeProof')}
                    onClick={() => document.getElementById(`proofInput-${record.id}`).click()}
                  >
                    <Upload size={14} />
                  </button>
                  <button
                    type="button"
                    className={styles.proofIconButton}
                    title={t('paymentVerification.deleteProof')}
                    onClick={() => handleDeleteProof(record)}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              )}
            </>
          ) : isLocked(record) ? (
            <span className={styles.noProofText}>{t('paymentVerification.noFileChosen')}</span>
          ) : (
            <button
              type="button"
              className={styles.uploadProofButton}
              onClick={() => document.getElementById(`proofInput-${record.id}`).click()}
              disabled={uploadingId === record.id}
            >
              <Upload size={14} />
              {uploadingId === record.id ? (t('common.uploading') || 'Uploading...') : t('paymentVerification.noFileChosen')}
            </button>
          )}
          <input
            id={`proofInput-${record.id}`}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            style={{ display: 'none' }}
            onChange={(e) => handleProofChange(record, e)}
          />
          {proofErrors[record.id] && <span className={styles.errorText}>{proofErrors[record.id]}</span>}
        </div>
      )
    },
    {
      key: 'status',
      header: t('paymentVerification.isVerified'),
      sortable: true,
      width: '130px',
      render: (record) => {
        const config = {
          unverified: { className: styles.unverifiedBadge, icon: <XCircle size={14} />, label: t('paymentVerification.unverified') },
          issues: { className: styles.issuesBadge, icon: <AlertTriangle size={14} />, label: t('paymentVerification.issues') },
          verified: { className: styles.verifiedBadge, icon: <CheckCircle2 size={14} />, label: t('paymentVerification.verified') },
          void: { className: styles.voidBadge, icon: <Ban size={14} />, label: t('paymentVerification.void') }
        }[record.status] || {};

        return (
          <span className={`${styles.statusBadge} ${config.className || ''}`}>
            {config.icon}
            {config.label || record.status}
          </span>
        );
      }
    },
    {
      key: 'createdByName',
      header: t('paymentVerification.createdBy'),
      render: (record) => record.createdBy?.name || '-'
    },
    {
      key: 'createdAt',
      header: t('paymentVerification.createdDate'),
      sortable: true,
      width: '130px',
      render: (record) => (
        <div className={styles.dateCell}>
          <div className={styles.dateInfo}><Calendar size={12} />{formatDate(record.createdAt)}</div>
          <div className={styles.timeInfo}>{formatTime(record.createdAt)}</div>
        </div>
      )
    },
    {
      key: 'verifiedByName',
      header: t('paymentVerification.verifiedBy'),
      render: (record) => record.verifiedBy?.name || '-'
    },
    {
      key: 'verifiedAt',
      header: t('paymentVerification.verifiedDate'),
      sortable: true,
      width: '130px',
      render: (record) =>
        record.verifiedAt ? (
          <div className={styles.dateCell}>
            <div className={styles.dateInfo}><Calendar size={12} />{formatDate(record.verifiedAt)}</div>
            <div className={styles.timeInfo}>{formatTime(record.verifiedAt)}</div>
          </div>
        ) : '-'
    },
    {
      key: 'voidedByName',
      header: t('paymentVerification.voidedBy'),
      render: (record) => record.voidedBy?.name || '-'
    },
    {
      key: 'voidedAt',
      header: t('paymentVerification.voidedDate'),
      sortable: true,
      width: '130px',
      render: (record) =>
        record.voidedAt ? (
          <div className={styles.dateCell}>
            <div className={styles.dateInfo}><Calendar size={12} />{formatDate(record.voidedAt)}</div>
            <div className={styles.timeInfo}>{formatTime(record.voidedAt)}</div>
          </div>
        ) : '-'
    }
  ];

  const tableActions = {
    width: '210px',
    frozen: true,
    render: (record) => (
      <div className={styles.actionsWrapper}>
        {isDirty(record) && !isLocked(record) && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.saveButton}`}
            title={t('paymentVerification.save')}
            onClick={() => handleSaveEdit(record)}
          >
            <Save size={14} />
          </button>
        )}
        {(record.status === 'unverified' || record.status === 'issues') && canVerify && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.verifyButton}`}
            title={getVerifyBlockReason(record) || t('paymentVerification.verify')}
            disabled={!!getVerifyBlockReason(record)}
            onClick={() => handleVerifyOne(record)}
          >
            <CheckCircle2 size={14} />
          </button>
        )}
        {record.status === 'unverified' && canVerify && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.flagButton}`}
            title={t('paymentVerification.flagAsIssue')}
            onClick={() => openFlagIssueModal(record)}
          >
            <AlertTriangle size={14} />
          </button>
        )}
        {record.status === 'issues' && canVerify && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.resolveButton}`}
            title={t('paymentVerification.resolve')}
            onClick={() => openResolveModal(record)}
          >
            <CheckCircle size={14} />
          </button>
        )}
        {record.status !== 'void' && canVoid && (
          <button
            type="button"
            className={`${styles.actionButton} ${styles.voidButton}`}
            title={t('paymentVerification.void')}
            onClick={() => openVoidModal(record)}
          >
            <Ban size={14} />
          </button>
        )}
        <button
          type="button"
          className={`${styles.actionButton} ${styles.notesButton}`}
          title={t('paymentVerification.notes')}
          onClick={() => setNotesModalRecord(record)}
        >
          <MessageSquare size={14} />
        </button>
      </div>
    )
  };

  if (loadingError) {
    return (
      <div className={styles.paymentVerificationPage}>
        <div className={styles.errorState}>
          <h3>{loadingError}</h3>
          <button onClick={fetchPaymentRecords} className={styles.retryButton}>
            <RefreshCw size={16} />
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.paymentVerificationPage}>
      <PageHeader
        title={t('paymentVerification.pageTitle')}
        description={t('paymentVerification.pageDescription')}
        icon={ShieldCheck}
      />

      {selectedIds.length > 0 && (
        <div className={styles.bulkActionsBar}>
          <div className={styles.bulkActionsLeft}>
            <span className={styles.selectedCount}>
              {selectedIds.length} {t('common.selected') || 'selected'}
            </span>
          </div>
          <div className={styles.bulkActionsRight}>
            <button
              onClick={handleBulkVerify}
              className={styles.bulkActionButton}
              disabled={unverifiedSelectedIds.length === 0}
            >
              <CheckCircle2 size={15} />
              {t('paymentVerification.verifySelected', { count: unverifiedSelectedIds.length })}
            </button>
            <button onClick={() => setSelectedIds([])} className={styles.bulkCancelButton}>
              {t('paymentVerification.clearSelection')}
            </button>
          </div>
        </div>
      )}

      <FilterPanel
        filters={filterDefinitions}
        values={filters}
        onChange={handleFilterChange}
        onClear={handleClearFilters}
        show={showFilters}
        onToggle={() => setShowFilters(!showFilters)}
        onExport={handleExport}
        showExport={true}
        exportButtonText={t('common.export')}
        searchConfig={{
          show: true,
          value: filters.search,
          onChange: handleSearchChange,
          placeholder: t('paymentVerification.searchPlaceholder')
        }}
        pageSizeConfig={{
          show: true,
          value: pagination.pageSize,
          onChange: handlePageSizeChange,
          options: pagination.pageSizeOptions
        }}
      />

      <DataTable
        data={paymentRecords}
        columns={columns}
        actions={tableActions}
        selectable
        selectedRows={selectedIds}
        onSelectionChange={setSelectedIds}
        sorting={sorting}
        onSortChange={setSorting}
        pagination={pagination}
        onPaginationChange={setPagination}
        loading={isLoading}
        emptyMessage={t('paymentVerification.noRecordsFound')}
        emptyDescription={t('paymentVerification.noRecordsFoundDescription')}
      />

      <ExportProgressModal
        isOpen={exportModalOpen}
        onClose={handleCloseExportModal}
        status={exportStatus}
        error={exportError}
        totalRecords={exportTotalRecords}
        fetchedRecords={exportFetchedRecords}
      />

      <PaymentReasonModal
        isOpen={!!reasonModal.mode}
        onClose={closeReasonModal}
        onConfirm={handleReasonConfirm}
        isLoading={isReasonSubmitting}
        {...reasonModalConfig}
      />

      <PaymentRecordNotesModal
        isOpen={!!notesModalRecord}
        onClose={() => setNotesModalRecord(null)}
        record={notesModalRecord}
      />
    </div>
  );
};

export default PaymentVerificationPage;
